import React, { useState, useEffect, useRef } from 'react';
import { authService } from '../services/api';

function ManageProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // CSV Import states
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvFile, setCSVFile] = useState(null);
  const [csvPreview, setCSVPreview] = useState([]);
  const [csvImporting, setCSVImporting] = useState(false);
  
  const [formData, setFormData] = useState({
    product_name: '',
    product_code: '',
    category: '',
    subtitle: '',
    description: '',
    price: '',
    min_stock_level: '',
    max_stock_level: '',
    current_stock: ''
  });

  const user = authService.getCurrentUser();

  useEffect(() => {
    loadProducts();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return null;
    }
    if (imagePath.startsWith('uploads/')) {
      return `https://b1.envicblue.co.uk/${imagePath}`;
    }
    return imagePath;
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://b1.envicblue.co.uk/api/products.php', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setProducts(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err) {
      console.error(err);
      showMessage('error', 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const optimizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxDimension = 800;
          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= 80000 || quality <= 0.1) {
                  resolve(blob);
                } else {
                  quality -= 0.1;
                  tryCompress();
                }
              },
              'image/jpeg',
              quality
            );
          };
          
          tryCompress();
        };
        
        img.onerror = reject;
      };
      
      reader.onerror = reject;
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file');
      return;
    }

    try {
      setUploading(true);
      const optimizedBlob = await optimizeImage(file);
      const optimizedFile = new File([optimizedBlob], file.name, { type: 'image/jpeg' });
      
      setImageFile(optimizedFile);
      setImagePreview(URL.createObjectURL(optimizedBlob));
      
      const sizeKB = (optimizedBlob.size / 1024).toFixed(2);
      showMessage('success', `✅ Image optimized to ${sizeKB}KB`);
    } catch (err) {
      console.error(err);
      showMessage('error', 'Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  // Camera Functions
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      showMessage('error', 'Unable to access camera');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      try {
        setUploading(true);
        const optimizedBlob = await optimizeImage(new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' }));
        const optimizedFile = new File([optimizedBlob], 'camera-photo.jpg', { type: 'image/jpeg' });
        
        setImageFile(optimizedFile);
        setImagePreview(URL.createObjectURL(optimizedBlob));
        
        const sizeKB = (optimizedBlob.size / 1024).toFixed(2);
        showMessage('success', `📸 Photo captured and optimized to ${sizeKB}KB`);
        stopCamera();
      } catch (err) {
        console.error(err);
        showMessage('error', 'Failed to process photo');
      } finally {
        setUploading(false);
      }
    }, 'image/jpeg');
  };

  // CSV Import Functions
  const handleCSVFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showMessage('error', 'Please select a CSV file');
      return;
    }

    setCSVFile(file);
    parseCSVPreview(file);
  };

  const parseCSVPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        showMessage('error', 'CSV file is empty');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const preview = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, i) => {
          row[header] = values[i] || '';
        });
        return row;
      });

      setCSVPreview(preview);
    };
    reader.readAsText(file);
  };

  const importCSV = async () => {
    if (!csvFile) {
      showMessage('error', 'Please select a CSV file');
      return;
    }

    setCSVImporting(true);

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await fetch('https://b1.envicblue.co.uk/api/products.php?action=import_csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', `✅ Successfully imported ${data.imported || 0} products`);
        setShowCSVModal(false);
        setCSVFile(null);
        setCSVPreview([]);
        loadProducts();
      } else {
        showMessage('error', `Failed to import: ${data.message}`);
      }
    } catch (err) {
      console.error(err);
      showMessage('error', 'Network error during import');
    } finally {
      setCSVImporting(false);
    }
  };

  const downloadCSVTemplate = () => {
    const template = `product_name,product_code,category,subtitle,description,price,min_stock_level,max_stock_level,current_stock
Paracetamol 500mg,PARA500,Medications,Pain Relief,Paracetamol tablets for pain and fever,5.99,50,200,100
Ibuprofen 200mg,IBU200,Medications,Anti-inflammatory,Ibuprofen for pain and inflammation,7.50,30,150,75
Alcohol Swabs,ASW200,Medical Supplies,Box of 200,Sterile alcohol swabs for cleaning,8.99,100,500,300`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        product_name: product.product_name,
        product_code: product.product_code,
        category: product.category || '',
        subtitle: product.subtitle || '',
        description: product.description || '',
        price: product.price,
        min_stock_level: product.min_stock_level,
        max_stock_level: product.max_stock_level,
        current_stock: product.current_stock
      });
      
      if (product.image_path) {
        setImagePreview(getImageUrl(product.image_path));
      }
    } else {
      setEditingProduct(null);
      setFormData({
        product_name: '',
        product_code: '',
        category: '',
        subtitle: '',
        description: '',
        price: '',
        min_stock_level: '',
        max_stock_level: '',
        current_stock: ''
      });
      setImageFile(null);
      setImagePreview(null);
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview(null);
    stopCamera();
    setFormData({
      product_name: '',
      product_code: '',
      category: '',
      subtitle: '',
      description: '',
      price: '',
      min_stock_level: '',
      max_stock_level: '',
      current_stock: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });

    if (imageFile) {
      formDataToSend.append('image', imageFile);
    }

    if (editingProduct) {
      formDataToSend.append('product_id', editingProduct.product_id);
    }

    try {
      const url = editingProduct 
        ? 'https://b1.envicblue.co.uk/api/products.php?action=update'
        : 'https://b1.envicblue.co.uk/api/products.php?action=create';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', editingProduct ? '✅ Product updated!' : '✅ Product created!');
        closeModal();
        loadProducts();
      } else {
        showMessage('error', `❌ ${data.message || 'Operation failed'}`);
      }
    } catch (err) {
      console.error(err);
      showMessage('error', '❌ Network error');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`https://b1.envicblue.co.uk/api/products.php?action=delete&product_id=${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', '✅ Product deleted!');
        loadProducts();
      } else {
        showMessage('error', `❌ ${data.message || 'Delete failed'}`);
      }
    } catch (err) {
      console.error(err);
      showMessage('error', '❌ Network error');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '48px' }}>⏳</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>
            🏥 Manage Products
          </h1>
          <p style={{ color: '#6b7280' }}>Add, edit, and manage medical products</p>
        </div>

        {/* Message */}
        {message.text && (
          <div style={{
            padding: '15px 20px',
            borderRadius: '12px',
            marginBottom: '20px',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `2px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <button
            onClick={() => openModal()}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            ➕ Add New Product
          </button>

          {(user?.role === 'super_admin' || user?.role === 'super_super_admin') && (
            <>
              <button
                onClick={() => setShowCSVModal(true)}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
                }}
              >
                📊 Import CSV
              </button>

              <button
                onClick={downloadCSVTemplate}
                style={{
                  padding: '14px 28px',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                📥 Download CSV Template
              </button>
            </>
          )}
        </div>

        {/* Products Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {products.map(product => (
            <div key={product.product_id} style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s'
            }}>
              {product.image_path && (
                <img 
                  src={getImageUrl(product.image_path)} 
                  alt={product.product_name}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    marginBottom: '15px'
                  }}
                />
              )}

              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                {product.product_name}
              </h3>
              
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                Code: {product.product_code}
              </p>

              {product.category && (
                <div style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  background: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#667eea',
                  marginBottom: '12px'
                }}>
                  📂 {product.category}
                </div>
              )}

              <p style={{ fontSize: '16px', fontWeight: '600', color: '#10b981', marginBottom: '15px' }}>
                £{parseFloat(product.price).toFixed(2)}
              </p>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => openModal(product)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(product.product_id)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={closeModal}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
              {editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                      Product Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.product_code}
                      onChange={(e) => setFormData({...formData, product_code: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                      Price (£) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., Medical Supplies, Equipment, Medications"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                    Description
                  </label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                      Min Stock
                    </label>
                    <input
                      type="number"
                      value={formData.min_stock_level}
                      onChange={(e) => setFormData({...formData, min_stock_level: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                      Max Stock
                    </label>
                    <input
                      type="number"
                      value={formData.max_stock_level}
                      onChange={(e) => setFormData({...formData, max_stock_level: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                      Current Stock
                    </label>
                    <input
                      type="number"
                      value={formData.current_stock}
                      onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                {/* Image Upload/Camera Section */}
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', fontSize: '14px' }}>
                    Product Image
                  </label>
                  
                  {imagePreview && !showCamera && (
                    <div style={{
                      marginBottom: '10px',
                      textAlign: 'center',
                      padding: '10px',
                      background: '#f9f9f9',
                      borderRadius: '8px'
                    }}>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'contain'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                        style={{
                          marginTop: '10px',
                          padding: '8px 16px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}

                  {showCamera ? (
                    <div style={{
                      background: '#000',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '10px'
                    }}>
                      <video 
                        ref={videoRef}
                        autoPlay 
                        playsInline
                        style={{
                          width: '100%',
                          maxHeight: '400px',
                          display: 'block'
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                      
                      <div style={{ 
                        padding: '15px',
                        background: '#1f2937',
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'center'
                      }}>
                        <button
                          type="button"
                          onClick={capturePhoto}
                          disabled={uploading}
                          style={{
                            padding: '12px 24px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          📸 Capture Photo
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          style={{
                            padding: '12px 24px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          ❌ Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <label style={{
                        flex: 1,
                        padding: '12px',
                        background: '#667eea',
                        color: 'white',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        📤 Upload Photo
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </label>

                      <button
                        type="button"
                        onClick={startCamera}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}
                      >
                        📷 Take Photo
                      </button>
                    </div>
                  )}

                  {uploading && (
                    <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '10px', textAlign: 'center' }}>
                      Processing image...
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {editingProduct ? '✏️ Update' : '➕ Create'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCSVModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowCSVModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
              📊 Import Products from CSV
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                Upload a CSV file with the following columns:
                <br />
                <code style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                  product_name, product_code, category, subtitle, description, price, min_stock_level, max_stock_level, current_stock
                </code>
              </p>

              <label style={{
                display: 'block',
                padding: '20px',
                background: '#f9fafb',
                border: '2px dashed #d1d5db',
                borderRadius: '12px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#667eea'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>📂</span>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#4b5563' }}>
                  {csvFile ? csvFile.name : 'Click to select CSV file'}
                </span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCSVFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {csvPreview.length > 0 && (
              <div style={{
                background: '#f9fafb',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                  Preview (First 5 rows):
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#e5e7eb' }}>
                        {Object.keys(csvPreview[0]).map(key => (
                          <th key={key} style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          {Object.values(row).map((val, i) => (
                            <td key={i} style={{ padding: '8px' }}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowCSVModal(false);
                  setCSVFile(null);
                  setCSVPreview([]);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={importCSV}
                disabled={!csvFile || csvImporting}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: csvFile && !csvImporting ? '#10b981' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: csvFile && !csvImporting ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
              >
                {csvImporting ? '⏳ Importing...' : '📊 Import Products'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageProducts;
